import {
  ConversionComparison,
  ConversionWirePeriod,
} from '@partner/feature-modules/dashboard/domain/model/conversion-metrics.model';
import { IsoDate } from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import {
  TrendBucket,
  TrendBucketUnit,
  TrendCountTile,
  TrendDaysTile,
  TrendMetrics,
  TrendTiles,
} from '@partner/feature-modules/dashboard/domain/model/trend-metrics.model';

/** 200 body of `GET api/dashboard/trend-metrics` (contract v1.1); instants are ISO-8601 UTC strings. */
export interface TrendMetricsResponse {
  asOfUtc: string;
  period: { from: IsoDate; to: IsoDate };
  comparison: TrendComparisonResponse;
  bucketUnit: TrendBucketUnit;
  bucketsTruncated: boolean;
  buckets: TrendBucketResponse[];
  tiles: TrendTilesResponse;
  /** Envelope symmetry with the sibling responses; always null on 200. */
  validationMessage: string | null;
}

export interface TrendComparisonResponse {
  period: ConversionWirePeriod;
  from: IsoDate;
  to: IsoDate;
  cutoffUtc: string;
}

export interface TrendBucketResponse {
  from: IsoDate;
  to: IsoDate;
  submitted: number;
  approved: number;
  funded: number;
}

export interface TrendCountTileResponse {
  value: number;
  priorValue: number;
  changePercent: number | null;
}

export interface TrendDaysTileResponse {
  value: number | null;
  priorValue: number | null;
  changeDays: number | null;
  changePercent: number | null;
  deals: number;
  priorDeals: number;
}

export interface TrendTilesResponse {
  submitted: TrendCountTileResponse;
  approved: TrendCountTileResponse;
  funded: TrendCountTileResponse;
  avgDaysToFund: TrendDaysTileResponse;
}

const adaptComparison = (comparison: TrendComparisonResponse): ConversionComparison => ({
  period: comparison.period,
  from: comparison.from,
  to: comparison.to,
  cutoffUtc: new Date(comparison.cutoffUtc),
});

const adaptBucket = (bucket: TrendBucketResponse): TrendBucket => ({
  from: bucket.from,
  to: bucket.to,
  submitted: bucket.submitted,
  approved: bucket.approved,
  funded: bucket.funded,
});

/** `??` turns a missing value into null and leaves a real 0 alone. */
const adaptCountTile = (tile: TrendCountTileResponse): TrendCountTile => ({
  value: tile.value,
  priorValue: tile.priorValue,
  changePercent: tile.changePercent ?? null,
});

const adaptDaysTile = (tile: TrendDaysTileResponse): TrendDaysTile => ({
  value: tile.value ?? null,
  priorValue: tile.priorValue ?? null,
  changeDays: tile.changeDays ?? null,
  changePercent: tile.changePercent ?? null,
  deals: tile.deals,
  priorDeals: tile.priorDeals,
});

const adaptTiles = (tiles: TrendTilesResponse): TrendTiles => ({
  submitted: adaptCountTile(tiles.submitted),
  approved: adaptCountTile(tiles.approved),
  funded: adaptCountTile(tiles.funded),
  avgDaysToFund: adaptDaysTile(tiles.avgDaysToFund),
});

export const adaptTrendMetrics = (response: TrendMetricsResponse): TrendMetrics => ({
  asOfUtc: new Date(response.asOfUtc),
  period: { from: response.period.from, to: response.period.to },
  comparison: adaptComparison(response.comparison),
  bucketUnit: response.bucketUnit,
  bucketsTruncated: response.bucketsTruncated,
  buckets: response.buckets.map(adaptBucket),
  tiles: adaptTiles(response.tiles),
});
